import { useState, useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { AdjuntoDoc, ApiResponse } from '@/types'
import { Loader } from '@/components/common/Loader'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { base64ToBlob, descargarBase64, getMimeType } from '@/utils/base64'

interface Props {
  dark: boolean
  /** ID del pedido (compra o venta) — solo para display y como query key */
  pedidoId: string
  onClose: () => void
  /** Función que obtiene la lista de adjuntos (incluye el binario base64) */
  fetchAdjuntos: (id: string) => Promise<ApiResponse<AdjuntoDoc[]>>
  /** Prefijo para aislar el caché de TanStack Query (p.ej. "compra" | "venta") */
  queryKeyPrefix?: string
}

const PREVIEWABLE = new Set(['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'])

function canPreview(adj: AdjuntoDoc) {
  return !!adj.contenido && PREVIEWABLE.has(adj.tipo.toLowerCase())
}

// ── Iconos ────────────────────────────────────────────────────────────────────

function FileIcon({ tipo, size = 'md' }: { tipo: string; size?: 'sm' | 'md' | 'lg' }) {
  const sz = size === 'lg' ? 'w-10 h-10' : size === 'sm' ? 'w-4 h-4' : 'w-[18px] h-[18px]'
  const clr: Record<string, string> = {
    pdf:  'text-red-400',  docx: 'text-blue-400', doc: 'text-blue-400',
    xlsx: 'text-emerald-400', xls: 'text-emerald-400',
    png:  'text-violet-400', jpg: 'text-violet-400', jpeg: 'text-violet-400',
    gif:  'text-violet-400', webp: 'text-violet-400',
    nota: 'text-amber-400',
  }
  const c = clr[tipo.toLowerCase()] ?? 'text-slate-400'
  const t = tipo.toLowerCase()

  if (t === 'nota') return (
    <svg className={`${sz} shrink-0 ${c}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H6a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2v-5"/>
      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  )

  if (t === 'pdf') return (
    <svg className={`${sz} shrink-0 ${c}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6"/>
      <path d="M9 15h1a1 1 0 000-2H9v4M15 13v4M12 13v4"/>
    </svg>
  )
  if (t === 'xlsx' || t === 'xls') return (
    <svg className={`${sz} shrink-0 ${c}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6"/>
      <path d="M8 13l2 3-2 3M16 13l-2 3 2 3"/>
    </svg>
  )
  if (t === 'docx' || t === 'doc') return (
    <svg className={`${sz} shrink-0 ${c}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6"/>
      <path d="M8 13h8M8 17h5"/>
    </svg>
  )
  if (['png','jpg','jpeg','gif','webp','bmp','svg'].includes(t)) return (
    <svg className={`${sz} shrink-0 ${c}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2"/>
      <circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/>
    </svg>
  )
  return (
    <svg className={`${sz} shrink-0 ${c}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6"/>
      <path d="M16 13H8M16 17H8M10 9H8"/>
    </svg>
  )
}

// ── Preview ───────────────────────────────────────────────────────────────────

function DocPreview({ adj, dark }: { adj: AdjuntoDoc; dark: boolean }) {
  const { t } = useTranslation()
  const mime     = getMimeType(adj.tipo)
  const isImage  = mime.startsWith('image/')
  const preview  = canPreview(adj)

  const objectUrl = useMemo(() => {
    if (!adj.contenido) return null
    try { return URL.createObjectURL(base64ToBlob(adj.contenido, mime)) }
    catch { return null }
  }, [adj.contenido, mime])

  useEffect(() => () => { if (objectUrl) URL.revokeObjectURL(objectUrl) }, [objectUrl])

  if (!preview || !objectUrl) {
    // ── Nota de texto — tipo 'nota' siempre muestra el visor, con o sin descripcion ──
    if (adj.tipo.toLowerCase() === 'nota' || adj.descripcion) return (
      <div className={`flex flex-col h-full overflow-auto p-6 ${dark ? 'bg-[#070a0f]' : 'bg-slate-50'}`}>
        {/* Cabecera nota */}
        <div className="flex items-center gap-2 mb-4 shrink-0">
          <div className={`p-2 rounded-xl ${dark ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-amber-50 border border-amber-200'}`}>
            <svg className="w-4 h-4 text-amber-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H6a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2v-5"/>
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
          </div>
          <div>
            <p className={`text-[12px] font-semibold ${dark ? 'text-slate-300' : 'text-slate-700'}`}>
              {t('attachments.noteTitle')}
            </p>
            <p className={`text-[10px] ${dark ? 'text-slate-600' : 'text-slate-400'}`}>
              {adj.nombre}
            </p>
          </div>
        </div>
        {/* Contenido de la nota */}
        <div className={`flex-1 rounded-xl p-5 text-[13px] leading-relaxed whitespace-pre-wrap border ${
          dark
            ? 'bg-slate-900 border-white/8 text-slate-200'
            : 'bg-white border-slate-200 text-slate-800 shadow-sm'
        }`}>
          {adj.descripcion
            ? adj.descripcion
            : <span className={`italic text-[12px] ${dark ? 'text-slate-600' : 'text-slate-400'}`}>
                {t('attachments.noteEmpty')}
              </span>
          }
        </div>
      </div>
    )

    // ── Sin previsualización ────────────────────────────────────────────────
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4 select-none relative overflow-hidden">
        <svg className="absolute inset-0 w-full h-full opacity-[0.04] pointer-events-none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="dots" x="0" y="0" width="24" height="24" patternUnits="userSpaceOnUse">
              <circle cx="1" cy="1" r="1" fill={dark ? '#94a3b8' : '#64748b'}/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#dots)"/>
        </svg>
        <div className={`p-4 rounded-2xl ${dark ? 'bg-white/5 border border-white/8' : 'bg-white border border-slate-200 shadow-sm'}`}>
          <FileIcon tipo={adj.tipo} size="lg" />
        </div>
        <div className="text-center z-10">
          <p className={`text-[13px] font-semibold ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
            {t('attachments.noPreviewTitle')}
          </p>
          <p className={`text-[11px] mt-1 ${dark ? 'text-slate-600' : 'text-slate-400'}`}>
            {t('attachments.noPreviewSub')}
          </p>
        </div>
      </div>
    )
  }

  if (isImage) return (
    <div className="flex items-center justify-center h-full p-8 overflow-auto">
      <img src={objectUrl} alt={adj.nombre}
        className="max-w-full max-h-full object-contain rounded-xl shadow-2xl"/>
    </div>
  )

  return <iframe src={objectUrl} title={adj.nombre} className="w-full h-full border-0"/>
}

// ── Componente principal ──────────────────────────────────────────────────────

export function AdjuntosModal({
  dark, pedidoId, onClose,
  fetchAdjuntos,
  queryKeyPrefix = 'adjuntos',
}: Props) {
  const { t } = useTranslation()
  const { isMobile } = useBreakpoint()
  const [open, setOpen]       = useState(false)
  // Selección por ÍNDICE (no por objeto): D365 permite adjuntos con nombre y
  // documentid idénticos; identificar por nombre dejaba el resaltado pegado al
  // primero. El índice en la lista es la identidad estable de cada adjunto.
  const [activeIdx, setActiveIdx] = useState<number>(-1)
  const [downloadError, setDownloadError] = useState<string | null>(null)

  // Animar entrada
  useEffect(() => {
    const id = requestAnimationFrame(() => setOpen(true))
    return () => cancelAnimationFrame(id)
  }, [])

  function handleClose() {
    setOpen(false)
    setTimeout(onClose, 340)
  }

  useEffect(() => {
    const fn = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose() }
    document.addEventListener('keydown', fn)
    return () => document.removeEventListener('keydown', fn)
  }, [])

  // Datos
  const { data, isLoading, isError, error } = useQuery({
    queryKey: [queryKeyPrefix, pedidoId],
    queryFn:  () => fetchAdjuntos(pedidoId),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })
  const adjuntos: AdjuntoDoc[] = (data?.data ?? []) as AdjuntoDoc[]

  // Selecciona el primero al cargar (o si el índice quedó fuera de rango).
  useEffect(() => {
    if (adjuntos.length > 0 && (activeIdx < 0 || activeIdx >= adjuntos.length)) setActiveIdx(0)
  }, [adjuntos])

  const activeAdj: AdjuntoDoc | null =
    activeIdx >= 0 && activeIdx < adjuntos.length ? adjuntos[activeIdx] : null

  // ── Descarga ──────────────────────────────────────────────────────────────
  // Siempre desde el binario base64 del listado (no hay flujo de descarga aparte).
  //
  // El revoke va diferido dentro del helper, no justo tras el clic: revocarlo al
  // momento dejaba la URL muerta antes de que el navegador leyera el blob, así
  // que con «preguntar dónde guardar» activado la descarga fallaba y el nombre
  // sugerido degeneraba en el UUID de la URL.
  function handleDownload(adj: AdjuntoDoc) {
    setDownloadError(null)
    if (!adj.contenido) return
    try {
      descargarBase64(
        adj.contenido,
        adj.nombreArchivo || adj.nombre,
        getMimeType(adj.tipo),
      )
    } catch {
      setDownloadError(t('attachments.downloadPrepareError'))
    }
  }

  // ── Tema ──────────────────────────────────────────────────────────────────
  const bg        = dark ? 'bg-[#0d1117]'      : 'bg-white'
  const sidebarBg = dark ? 'bg-[#161b22]'      : 'bg-slate-50'
  const border    = dark ? 'border-white/[0.07]': 'border-slate-200'
  const mainTxt   = dark ? 'text-slate-100'     : 'text-slate-900'
  const mutedTxt  = dark ? 'text-slate-400'     : 'text-slate-500'
  const dimTxt    = dark ? 'text-slate-600'     : 'text-slate-300'
  const previewBg = dark ? 'bg-[#070a0f]'       : 'bg-slate-100'

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end">

      {/* ── Backdrop ──────────────────────────────────────────────────────── */}
      <div
        onClick={handleClose}
        className="absolute inset-0 cursor-pointer"
        style={{
          backgroundColor: open ? 'rgba(0,0,0,0.52)' : 'rgba(0,0,0,0)',
          backdropFilter: open ? 'blur(3px)' : 'blur(0px)',
          transition: 'background-color 0.34s ease, backdrop-filter 0.34s ease',
        }}
      />

      {/* ── Panel lateral ─────────────────────────────────────────────────── */}
      <div
        className={`relative flex flex-col h-full shadow-2xl ${bg} border-l ${border}`}
        style={{
          width: isMobile ? '100vw' : 'min(92vw, 1040px)',
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.34s cubic-bezier(0.32, 0, 0.1, 1)',
          boxShadow: dark
            ? '-32px 0 80px rgba(0,0,0,0.6), -1px 0 0 rgba(255,255,255,0.05)'
            : '-32px 0 80px rgba(0,0,0,0.12), -1px 0 0 rgba(0,0,0,0.06)',
        }}
        role="dialog"
        aria-modal="true"
        aria-label={`${t('attachments.title')} — ${pedidoId}`}
      >

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className={`flex items-center gap-4 px-6 py-4 border-b ${border} shrink-0`}>
          {/* Icono + título */}
          <div className={`p-2 rounded-lg ${dark ? 'bg-white/5' : 'bg-slate-100'}`}>
            <svg className={`w-4 h-4 ${mutedTxt}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h2 className={`text-[13px] font-semibold ${mainTxt}`}>{t('attachments.title')}</h2>
            <p className={`text-[11px] ${mutedTxt} font-mono mt-0.5`}>
              {pedidoId}
              {!isLoading && !isError && (
                <span className={`not-italic font-sans ${dimTxt}`}>
                  {' '}· {t('attachments.file', { count: adjuntos.length })}
                </span>
              )}
            </p>
          </div>
          {/* Cerrar */}
          <button
            onClick={handleClose}
            aria-label={t('attachments.close')}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] transition-colors cursor-pointer ${
              dark ? 'text-slate-500 hover:text-slate-300 hover:bg-white/6' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
            }`}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
            <span className={`text-[10px] font-mono hidden sm:inline ${dimTxt}`}>ESC</span>
          </button>
        </div>

        {/* ── Tira de archivos mobile (md:hidden) ──────────────────────────── */}
        {isLoading && (
          <div className={`flex md:hidden items-center gap-2 px-4 py-2.5 border-b ${border} shrink-0 ${sidebarBg}`}>
            <svg className="w-4 h-4 animate-spin text-sky-500" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3"/>
              <path className="opacity-80" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
            </svg>
            <span className={`text-[11px] ${mutedTxt}`}>{t('attachments.loading')}</span>
          </div>
        )}
        {!isLoading && !isError && adjuntos.length > 0 && (
          <div className={`flex md:hidden overflow-x-auto gap-2 px-3 py-2 border-b ${border} shrink-0 ${sidebarBg}`}
               style={{ scrollbarWidth: 'none' }}>
            {adjuntos.map((adj, i) => {
              const isActive = activeIdx === i
              return (
                <button
                  key={`mob-${adj.orden ?? adj.nombreArchivo}-${i}`}
                  onClick={() => setActiveIdx(i)}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[11px] font-medium whitespace-nowrap transition-colors cursor-pointer shrink-0 ${
                    isActive
                      ? dark
                        ? 'bg-sky-500/15 border-sky-500/30 text-sky-300'
                        : 'bg-sky-50 border-sky-200 text-sky-700'
                      : dark
                        ? 'border-white/10 bg-white/5 text-slate-400 hover:bg-white/10'
                        : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'
                  }`}
                >
                  <FileIcon tipo={adj.tipo} size="sm" />
                  <span className="max-w-[120px] truncate">{adj.nombre}</span>
                  {isActive && <div className="w-1.5 h-1.5 rounded-full bg-sky-500 shrink-0 ml-0.5"/>}
                </button>
              )
            })}
          </div>
        )}

        {/* ── Cuerpo ──────────────────────────────────────────────────────── */}
        <div className="flex flex-1 min-h-0">

          {/* Sidebar de archivos — oculto en mobile */}
          <div className={`hidden md:flex flex-col w-56 shrink-0 border-r ${border} ${sidebarBg}`}>

            {/* Loading */}
            {isLoading && (
              <div className="flex flex-col items-center justify-center flex-1">
                <Loader label={t('attachments.loading')} size={11} />
              </div>
            )}

            {/* Error */}
            {isError && !isLoading && (
              <div className="flex flex-col items-center justify-center flex-1 gap-2 px-5 text-center">
                <svg className="w-6 h-6 text-red-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01" strokeLinecap="round"/>
                </svg>
                <p className={`text-[11px] font-semibold ${dark ? 'text-red-400' : 'text-red-500'}`}>
                  {t('attachments.errorTitle')}
                </p>
                <p className={`text-[10px] ${mutedTxt}`}>
                  {(error as Error)?.message?.includes('502')
                    ? t('attachments.error502')
                    : t('attachments.errorServer')}
                </p>
              </div>
            )}

            {/* Sin archivos */}
            {!isLoading && !isError && adjuntos.length === 0 && (
              <div className="flex flex-col items-center justify-center flex-1 gap-2 px-5 text-center">
                <svg className={`w-7 h-7 ${dimTxt}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8"/>
                </svg>
                <p className={`text-[12px] font-semibold ${mainTxt}`}>{t('attachments.noDocuments')}</p>
                <p className={`text-[10px] ${mutedTxt}`}>{t('attachments.noDocumentsSub')}</p>
              </div>
            )}

            {/* Lista */}
            {!isLoading && !isError && adjuntos.length > 0 && (
              <nav className="flex-1 overflow-y-auto py-2 px-2 space-y-0.5">
                {adjuntos.map((adj, i) => {
                  const isActive = activeIdx === i
                  return (
                    <button
                      key={`${adj.orden ?? adj.nombreArchivo}-${i}`}
                      onClick={() => setActiveIdx(i)}
                      className={`w-full text-left flex items-center gap-2.5 px-3 py-2.5 rounded-xl transition-all duration-150 cursor-pointer group ${
                        isActive
                          ? dark
                            ? 'bg-sky-500/12 ring-1 ring-sky-500/25'
                            : 'bg-sky-50 ring-1 ring-sky-200'
                          : dark
                            ? 'hover:bg-white/5'
                            : 'hover:bg-slate-100'
                      }`}
                    >
                      <FileIcon tipo={adj.tipo} size="sm" />
                      <div className="flex-1 min-w-0">
                        <p className={`text-[11.5px] font-medium leading-snug truncate transition-colors ${
                          isActive
                            ? dark ? 'text-sky-300' : 'text-sky-700'
                            : dark ? 'text-slate-300 group-hover:text-slate-100' : 'text-slate-600 group-hover:text-slate-800'
                        }`} title={adj.nombre}>
                          {adj.nombre}
                        </p>
                        <p className={`text-[9.5px] uppercase font-bold tracking-wide mt-0.5 ${
                          isActive ? (dark ? 'text-sky-500' : 'text-sky-500') : dimTxt
                        }`}>
                          {adj.tipo}
                        </p>
                      </div>
                      {/* Indicador activo */}
                      {isActive && (
                        <div className="w-1 h-1 rounded-full bg-sky-500 shrink-0"/>
                      )}
                    </button>
                  )
                })}
              </nav>
            )}
          </div>

          {/* ── Panel de visualización ───────────────────────────────────── */}
          <div className="flex flex-col flex-1 min-w-0">

            {activeAdj ? (
              <>
                {/* Barra de herramientas */}
                <div className={`flex items-center justify-between px-5 py-3 border-b ${border} shrink-0 ${dark ? 'bg-[#0d1117]/80' : 'bg-white'}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <FileIcon tipo={activeAdj.tipo} size="sm" />
                    <p className={`text-[12.5px] font-semibold truncate ${mainTxt}`} title={activeAdj.nombre}>
                      {activeAdj.nombre}
                    </p>
                    <span className={`hidden md:inline-flex shrink-0 text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-md border ${
                      dark ? 'border-white/10 text-slate-400 bg-white/5' : 'border-slate-200 text-slate-500 bg-slate-50'
                    }`}>
                      {activeAdj.tipo}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 shrink-0 ml-4">
                    {/* Navegación anterior / siguiente */}
                    {adjuntos.length > 1 && (
                      <div className={`flex items-center rounded-lg border overflow-hidden ${border}`}>
                        <button
                          onClick={() => activeIdx > 0 && setActiveIdx(activeIdx - 1)}
                          disabled={activeIdx === 0}
                          title={t('attachments.previous')}
                          className={`px-2.5 py-1.5 transition-colors cursor-pointer disabled:opacity-25 disabled:cursor-not-allowed ${
                            dark ? 'hover:bg-white/8 text-slate-400' : 'hover:bg-slate-100 text-slate-500'
                          }`}
                        >
                          <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M10 12L6 8l4-4"/>
                          </svg>
                        </button>
                        <span className={`px-2.5 text-[10.5px] font-medium border-x tabular-nums ${border} ${mutedTxt} select-none`}>
                          {activeIdx + 1} / {adjuntos.length}
                        </span>
                        <button
                          onClick={() => activeIdx < adjuntos.length - 1 && setActiveIdx(activeIdx + 1)}
                          disabled={activeIdx === adjuntos.length - 1}
                          title={t('attachments.next')}
                          className={`px-2.5 py-1.5 transition-colors cursor-pointer disabled:opacity-25 disabled:cursor-not-allowed ${
                            dark ? 'hover:bg-white/8 text-slate-400' : 'hover:bg-slate-100 text-slate-500'
                          }`}
                        >
                          <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M6 12l4-4-4-4"/>
                          </svg>
                        </button>
                      </div>
                    )}

                    {/* Descargar — solo si el adjunto trae binario (sin fallback) */}
                    {!!activeAdj.contenido && (
                      <button
                        onClick={() => handleDownload(activeAdj)}
                        className={`flex items-center gap-1.5 text-[11.5px] font-medium px-3.5 py-1.5 rounded-lg transition-all duration-150 cursor-pointer ${
                          dark
                            ? 'bg-sky-500/12 text-sky-400 hover:bg-sky-500/20 border border-sky-500/20'
                            : 'bg-sky-600 text-white hover:bg-sky-700 shadow-sm'
                        }`}
                      >
                        <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M8 3v7M5 7l3 3 3-3M3 13h10"/>
                        </svg>
                        <span className="hidden md:inline">{t('attachments.download')}</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Error descarga */}
                {downloadError && (
                  <div className={`mx-4 mt-3 shrink-0 text-[11px] px-3 py-2 rounded-lg border ${
                    dark ? 'bg-red-500/10 text-red-400 border-red-500/20' : 'bg-red-50 text-red-600 border-red-200'
                  }`}>
                    {downloadError}
                  </div>
                )}

                {/* Área de visualización */}
                <div className={`flex-1 min-h-0 relative ${previewBg}`}>
                  <DocPreview adj={activeAdj} dark={dark} />
                </div>
              </>
            ) : (
              /* Viewer vacío */
              <div className={`flex-1 flex flex-col items-center justify-center gap-3 ${previewBg} select-none`}>
                {!isLoading && !isError && adjuntos.length > 0 && (
                  <>
                    <svg className={`w-8 h-8 ${dimTxt}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round">
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8"/>
                    </svg>
                    <p className={`text-[12px] ${dimTxt}`}>{t('attachments.selectDocument')}</p>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
